import { FC } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider, FieldPath } from 'react-hook-form';
import useReactRouter from 'use-react-router';

import Button from 'jbc-front/components/Button';
import { ButtonRow } from 'jbc-front/components/presenters/layout/ButtonRow';

import { useEmployeeMutation as useMutation } from '@jbc-year-end-adj/2024/hooks/graphql/useMutation';
import { eachValidationErrorsForHookFrom, ServerValidationError } from '@jbc-year-end-adj/2024/hooks/graphql/error/ServerValidationError';
import { useNotify } from '@jbc-year-end-adj/common/hooks/useNotify';

import { FormSection } from '@jbc-year-end-adj/2024/components/form/FormSection';
import { FileField } from '@jbc-year-end-adj/2024/components/react-hook-form/FileField';
import { FilePreview } from '@jbc-year-end-adj/2024/features/FilePreview';
import { AmountField } from '@jbc-year-end-adj/2024/components/react-hook-form/AmountField';

import { premiumSchema, Schema, generateDefaultValues } from './schema';
import { ANSWER_PREMIUM, convertFormDataToQueryVariables } from './mutation';
import { Request } from './query';

import styles from './Premium.scss';

type FormProps = {
  request: Request;
  imageSetting: 'none' | 'optional' | 'required';
};

export const Form: FC<FormProps> = ({ request, imageSetting }) => {
  const [update, { loading }] = useMutation(ANSWER_PREMIUM);
  const { history } = useReactRouter();
  const notify = useNotify();

  const imageRequired = imageSetting === 'required';
  const methods = useForm<Schema>({
    defaultValues: generateDefaultValues(request.profile.premium),
    resolver: yupResolver(premiumSchema(imageRequired)),
    mode: 'onSubmit'
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    setError,
    watch
  } = methods;

  const onSubmit = async (data: Schema) => {
    if (
      [data.smallAndMediumSizedEnterprises, data.corporatePension, data.personalPension, data.disabilityMutualAid].filter(v => v).length < 1
    ) {
      notify('今年支払った小規模企業共済等掛金情報を記入してください', 'error');
      return;
    }

    const variables = convertFormDataToQueryVariables(data);
    try {
      await update({ variables });
      history.push('/employee_input');
    } catch (err) {
      if (err instanceof ServerValidationError) {
        eachValidationErrorsForHookFrom(err.validationErrors, premiumSchema(imageRequired), data, (path, error) => {
          setError(path as FieldPath<Schema>, error);
        });
        return;
      }
      throw err;
    }
  };

  const smallAndMediumSizedEnterprisesImage = watch('smallAndMediumSizedEnterprisesImage');
  const corporatePensionImage = watch('corporatePensionImage');
  const personalPensionImage = watch('personalPensionImage');
  const disabilityMutualAidImage = watch('disabilityMutualAidImage');

  return (
    <FormProvider {...methods}>
      {imageSetting === 'required' && (
        <div className={styles.cautionMessage}>
          <h3>支払った掛金の証明書画像を添付してください。</h3>
          <h3>支払っていない掛金については、画像の添付は不要です。</h3>
        </div>
      )}
      <form>
        <FormSection>
          <AmountField
            label="独立行政法人中小企業基盤整備機構の共済契約の掛金"
            placeholder="掛金がある場合に入力してください　例：100000"
            {...register(`smallAndMediumSizedEnterprises`)}
            error={errors.smallAndMediumSizedEnterprises && errors.smallAndMediumSizedEnterprises?.message}
            isError={errors.smallAndMediumSizedEnterprises && !!errors.smallAndMediumSizedEnterprises?.message}
          />
          {imageSetting !== 'none' && (
            <FileField
              label="独立行政法人中小企業基盤整備機構の共済契約の掛金 証明書画像"
              note="対応可能形式：jpg, jpeg, gif, png, pdf"
              name={`smallAndMediumSizedEnterprisesImage`}
              required={imageSetting === 'required'}
              error={errors.smallAndMediumSizedEnterprisesImage && errors.smallAndMediumSizedEnterprisesImage.message}
            >
              {smallAndMediumSizedEnterprisesImage && (
                <FileField.FileContainer>
                  <FileField.File
                    file={smallAndMediumSizedEnterprisesImage}
                    onRemove={() => setValue(`smallAndMediumSizedEnterprisesImage`, undefined)}
                  />
                  <FilePreview file={smallAndMediumSizedEnterprisesImage} />
                </FileField.FileContainer>
              )}
            </FileField>
          )}

          <AmountField
            label="確定拠出年金法に規定する企業型年金加入者掛金"
            placeholder="企業型年金の掛金がある場合に入力してください　例：100000"
            {...register(`corporatePension`)}
            error={errors.corporatePension && errors.corporatePension.message}
            isError={errors.corporatePension && !!errors.corporatePension.message}
          />
          {imageSetting !== 'none' && (
            <FileField
              label="確定拠出年金法に規定する企業型年金加入者掛金 証明書画像"
              note="対応可能形式：jpg, jpeg, gif, png, pdf"
              name={`corporatePensionImage`}
              required={imageSetting === 'required'}
              error={errors.corporatePensionImage && errors.corporatePensionImage.message}
            >
              {corporatePensionImage && (
                <FileField.FileContainer>
                  <FileField.File file={corporatePensionImage} onRemove={() => setValue(`corporatePensionImage`, undefined)} />
                  <FilePreview file={corporatePensionImage} />
                </FileField.FileContainer>
              )}
            </FileField>
          )}

          <AmountField
            label="確定拠出年金法に規定する個人型年金加入者掛金"
            placeholder="個人型年金（iDeCo）の掛金がある場合に入力してください　例：100000"
            {...register(`personalPension`)}
            error={errors.personalPension && errors.personalPension.message}
            isError={errors.personalPension && !!errors.personalPension.message}
          />
          {imageSetting !== 'none' && (
            <FileField
              label="確定拠出年金法に規定する個人型年金加入者掛金 証明書画像"
              note="対応可能形式：jpg, jpeg, gif, png, pdf"
              name={`personalPensionImage`}
              required={imageSetting === 'required'}
              error={errors.personalPensionImage && errors.personalPensionImage.message}
            >
              {personalPensionImage && (
                <FileField.FileContainer>
                  <FileField.File file={personalPensionImage} onRemove={() => setValue(`personalPensionImage`, undefined)} />
                  <FilePreview file={personalPensionImage} />
                </FileField.FileContainer>
              )}
            </FileField>
          )}

          <AmountField
            label="心身障害者扶養共済制度に関する契約の掛金"
            placeholder="掛金がある場合に入力してください　例：100000"
            {...register(`disabilityMutualAid`)}
            error={errors.disabilityMutualAid && errors.disabilityMutualAid.message}
            isError={errors.disabilityMutualAid && !!errors.disabilityMutualAid.message}
          />
          {imageSetting !== 'none' && (
            <FileField
              label="心身障害者扶養共済制度に関する契約の掛金 証明書画像"
              note="対応可能形式：jpg, jpeg, gif, png, pdf"
              name={`disabilityMutualAidImage`}
              required={imageSetting === 'required'}
              error={errors.disabilityMutualAidImage && errors.disabilityMutualAidImage.message}
            >
              {disabilityMutualAidImage && (
                <FileField.FileContainer>
                  <FileField.File file={disabilityMutualAidImage} onRemove={() => setValue(`disabilityMutualAidImage`, undefined)} />
                  <FilePreview file={disabilityMutualAidImage} />
                </FileField.FileContainer>
              )}
            </FileField>
          )}

          <ButtonRow>
            <Button primary onClick={handleSubmit(onSubmit)} disabled={loading}>
              次へ
            </Button>
          </ButtonRow>
        </FormSection>
      </form>
    </FormProvider>
  );
};
